import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { fmtMoney } from "@/components/Shared";
import PageShell from "@/components/PageShell";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { Button } from "@/components/ui/button";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import CalendarDayModal from "@/components/CalendarDayModal";

const monthsEs = ["enero","febrero","marzo","abril","mayo","junio","julio","agosto","septiembre","octubre","noviembre","diciembre"];

const locale = {
  localize: {
    month: (n) => monthsEs[n].charAt(0).toUpperCase() + monthsEs[n].slice(1),
    day: (n) => ["dom","lun","mar","mié","jue","vie","sáb"][n],
    ordinalNumber: (n) => String(n),
    era: () => "",
    quarter: () => "",
    dayPeriod: () => "",
  },
  formatLong: {},
  options: { weekStartsOn: 1 },
};

function toKey(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function CalendarPage() {
  const { user } = useAuth();
  const [month, setMonth] = useState(new Date());
  const [selected, setSelected] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [monthCounts, setMonthCounts] = useState({});
  const seesFinance = user?.role !== "customer_service";

  const load = async (d) => {
    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    const requests = [
      api.get(`/calendar/month-events?year=${y}&month=${m}`).then((r) => setMonthCounts(r.data || {})),
    ];
    if (seesFinance) {
      requests.push(api.get(`/calendar/invoices?year=${y}&month=${m}`).then((r) => setInvoices(r.data || [])));
    } else {
      setInvoices([]);
    }
    try { await Promise.all(requests); } catch (_) { /* silent */ }
  };
  useEffect(() => { load(month); /* eslint-disable-next-line */ }, [month, seesFinance]);

  const byDate = useMemo(() => {
    const map = {};
    invoices.forEach((inv) => {
      if (!map[inv.due_date]) map[inv.due_date] = [];
      map[inv.due_date].push(inv);
    });
    return map;
  }, [invoices]);

  const modifiers = useMemo(() => {
    const toDates = (filter) => Object.keys(byDate).filter(filter).map((d) => new Date(d + "T00:00:00"));
    return {
      overdue: toDates((d) => byDate[d].some((i) => i.status === "overdue")),
      pending: toDates((d) => byDate[d].every((i) => i.status !== "overdue") && byDate[d].some((i) => i.status === "pending")),
      paid: toDates((d) => byDate[d].every((i) => i.status === "paid")),
    };
  }, [byDate]);

  const totals = useMemo(() => {
    const tot = { pending: 0, overdue: 0, paid: 0 };
    invoices.forEach((i) => { tot[i.status] = (tot[i.status] || 0) + i.amount; });
    return tot;
  }, [invoices]);

  const goPrev = () => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1));
  const goNext = () => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1));
  const goToday = () => { const t = new Date(); setMonth(new Date(t.getFullYear(), t.getMonth(), 1)); setSelected(t); setModalOpen(true); };

  const handleDayClick = (day) => {
    if (!day) return;
    setSelected(day);
    setModalOpen(true);
  };

  // Custom day cell that renders the event-count badge on top
  const DayContent = ({ date }) => {
    const key = toKey(date);
    const count = monthCounts[key] || 0;
    return (
      <div className="relative w-full h-full flex items-center justify-center">
        <span>{date.getDate()}</span>
        {count > 0 && (
          <span
            data-testid={`day-count-${key}`}
            className="absolute top-1 right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-slate-900 text-white dark:bg-white dark:text-slate-900 text-[9px] font-mono font-semibold flex items-center justify-center leading-none"
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </div>
    );
  };

  return (
    <PageShell
      section="Operación"
      page="Calendario"
      context={`${monthsEs[month.getMonth()]} ${month.getFullYear()}`}
      testid="calendar-toolbar"
      actions={
        <div className="flex items-center gap-2" data-testid="calendar-toolbar-actions">
          <Button variant="outline" size="sm" onClick={goPrev} data-testid="cal-prev"><CaretLeft size={14} /></Button>
          <Button variant="outline" size="sm" onClick={goToday} data-testid="cal-today">Hoy</Button>
          <Button variant="outline" size="sm" onClick={goNext} data-testid="cal-next"><CaretRight size={14} /></Button>
        </div>
      }
    >

      {/* Monthly totals strip — hidden for customer_service */}
      {seesFinance && (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md mb-6 grid grid-cols-2 md:grid-cols-4">
          <div className="px-5 py-4 border-r border-slate-200 dark:border-slate-800">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 font-semibold">Mes en curso</div>
            <div className="text-base font-semibold text-slate-900 dark:text-white mt-1 capitalize">{monthsEs[month.getMonth()]} {month.getFullYear()}</div>
          </div>
          <div className="px-5 py-4 border-r border-slate-200 dark:border-slate-800">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 font-semibold">A cobrar (pendientes)</div>
            <div className="text-base font-mono font-semibold text-amber-700 dark:text-amber-400 mt-1">{fmtMoney(totals.pending || 0)}</div>
          </div>
          <div className="px-5 py-4 border-r border-slate-200 dark:border-slate-800">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 font-semibold">Vencidas</div>
            <div className="text-base font-mono font-semibold text-red-600 dark:text-red-400 mt-1">{fmtMoney(totals.overdue || 0)}</div>
          </div>
          <div className="px-5 py-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 font-semibold">Cobradas</div>
            <div className="text-base font-mono font-semibold text-green-700 dark:text-green-400 mt-1">{fmtMoney(totals.paid || 0)}</div>
          </div>
        </div>
      )}

      {/* Calendar (full width) */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md p-6" data-testid="calendar-grid">
        <DayPicker
          mode="single"
          month={month}
          onMonthChange={setMonth}
          selected={selected}
          onSelect={handleDayClick}
          modifiers={modifiers}
          modifiersClassNames={{
            overdue: "!bg-red-50 dark:!bg-red-950/40 !text-red-700 dark:!text-red-400 !font-semibold",
            pending: "!bg-amber-50 dark:!bg-amber-950/40 !text-amber-700 dark:!text-amber-400 !font-semibold",
            paid: "!bg-green-50 dark:!bg-green-950/40 !text-green-700 dark:!text-green-400",
          }}
          components={{ DayContent }}
          classNames={{
            months: "w-full",
            month: "w-full",
            caption: "flex justify-center pt-1 pb-4 relative items-center",
            caption_label: "text-base font-semibold text-slate-900 dark:text-white capitalize",
            nav: "hidden",
            table: "w-full border-collapse",
            head_row: "grid grid-cols-7",
            head_cell: "text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-500 font-semibold pb-2",
            row: "grid grid-cols-7 mt-1",
            cell: "p-1 text-center",
            day: "h-16 w-full rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 text-sm transition-colors flex items-center justify-center cursor-pointer",
            day_selected: "!bg-slate-900 dark:!bg-white !text-white dark:!text-slate-900 !font-semibold hover:!bg-slate-900",
            day_today: "ring-1 ring-slate-900 dark:ring-white text-slate-900 dark:text-white font-semibold",
            day_outside: "text-slate-300 dark:text-slate-700",
          }}
          locale={locale}
        />
        <div className="flex flex-wrap items-center gap-5 mt-4 pt-4 border-t border-slate-100 dark:border-slate-800 text-xs text-slate-600 dark:text-slate-400">
          {seesFinance && <>
            <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-red-100 dark:bg-red-950" /> Vencidas</span>
            <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-amber-100 dark:bg-amber-950" /> Pendientes</span>
            <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-green-100 dark:bg-green-950" /> Pagadas</span>
          </>}
          <span className="inline-flex items-center gap-2">
            <span className="inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-[9px] font-mono font-semibold">3</span>
            Eventos del día
          </span>
          <span className="inline-flex items-center gap-2 text-slate-500 dark:text-slate-500">Toca un día para ver el detalle completo</span>
        </div>
      </div>

      <CalendarDayModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        date={selected ? toKey(selected) : null}
        userRole={user?.role}
      />
    </PageShell>
  );
}
